type Point = {
  x: number
  y: number
}

type Direction = 'horizontal-first' | 'vertical-first'

type RoundedConnectorOptions = {
  radius?: number
  direction?: Direction
}

/**
 * Creates an SVG path from point A to point B
 * with exactly one rounded 90deg corner.
 *
 * Example output:
 * M 10 20 L 60 20 Q 80 20 80 40 L 80 100
 */
export function createRoundedConnectorPath(
  a: Point,
  b: Point,
  options: RoundedConnectorOptions = {},
): string {
  const { radius = 12, direction = 'horizontal-first' } = options

  if (a.x === b.x || a.y === b.y) {
    // No corner needed, straight line
    return `M ${a.x} ${a.y} L ${b.x} ${b.y}`
  }

  if (direction === 'horizontal-first') {
    return createHorizontalFirstPath(a, b, radius)
  }

  return createVerticalFirstPath(a, b, radius)
}

function createHorizontalFirstPath(
  a: Point,
  b: Point,
  requestedRadius: number,
): string {
  const corner = { x: b.x, y: a.y }

  const dx = b.x - a.x
  const dy = b.y - a.y

  const sx = Math.sign(dx)
  const sy = Math.sign(dy)

  const horizontalLength = Math.abs(dx)
  const verticalLength = Math.abs(dy)

  const r = Math.min(requestedRadius, horizontalLength / 2, verticalLength / 2)

  const p1 = { x: corner.x - sx * r, y: corner.y }
  const p2 = { x: corner.x, y: corner.y + sy * r }

  return [
    `M ${a.x} ${a.y}`,
    `L ${p1.x} ${p1.y}`,
    `Q ${corner.x} ${corner.y} ${p2.x} ${p2.y}`,
    `L ${b.x} ${b.y}`,
  ].join(' ')
}

function createVerticalFirstPath(
  a: Point,
  b: Point,
  requestedRadius: number,
): string {
  const corner = { x: a.x, y: b.y }

  const dx = b.x - a.x
  const dy = b.y - a.y

  const sx = Math.sign(dx)
  const sy = Math.sign(dy)

  const horizontalLength = Math.abs(dx)
  const verticalLength = Math.abs(dy)

  const r = Math.min(requestedRadius, horizontalLength / 2, verticalLength / 2)

  const p1 = { x: corner.x, y: corner.y - sy * r }
  const p2 = { x: corner.x + sx * r, y: corner.y }

  return [
    `M ${a.x} ${a.y}`,
    `L ${p1.x} ${p1.y}`,
    `Q ${corner.x} ${corner.y} ${p2.x} ${p2.y}`,
    `L ${b.x} ${b.y}`,
  ].join(' ')
}
